import { motion } from 'framer-motion';

interface WelcomeScreenProps {
  onStart: () => void;
}

export function WelcomeScreen({ onStart }: WelcomeScreenProps) {

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="absolute inset-0 z-[100] flex flex-col items-center justify-center cursor-pointer overflow-hidden"
      onClick={onStart}
      onTouchStart={onStart}
    >
      {/* Deep dark background */}
      <div className="absolute inset-0 bg-[#030712]" />

      {/* Star/particle field - CSS only */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Layer 1: Small stars */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(1px 1px at 40% 70%, rgba(255,255,255,0.2) 0%, transparent 100%),
              radial-gradient(1px 1px at 60% 20%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(1px 1px at 80% 50%, rgba(255,255,255,0.2) 0%, transparent 100%),
              radial-gradient(1px 1px at 10% 80%, rgba(255,255,255,0.15) 0%, transparent 100%),
              radial-gradient(1px 1px at 90% 10%, rgba(255,255,255,0.2) 0%, transparent 100%),
              radial-gradient(1.5px 1.5px at 30% 50%, rgba(0,184,169,0.3) 0%, transparent 100%),
              radial-gradient(1.5px 1.5px at 70% 80%, rgba(0,184,169,0.2) 0%, transparent 100%),
              radial-gradient(1px 1px at 50% 10%, rgba(255,255,255,0.25) 0%, transparent 100%),
              radial-gradient(1px 1px at 15% 55%, rgba(255,255,255,0.2) 0%, transparent 100%),
              radial-gradient(1px 1px at 85% 35%, rgba(255,255,255,0.15) 0%, transparent 100%),
              radial-gradient(1px 1px at 45% 90%, rgba(0,184,169,0.15) 0%, transparent 100%)
            `,
            animation: 'welcomeStarDrift 60s linear infinite',
          }}
        />
        {/* Layer 2: Larger dim stars */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: `
              radial-gradient(2px 2px at 25% 25%, rgba(255,255,255,0.4) 0%, transparent 100%),
              radial-gradient(2px 2px at 75% 75%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(2px 2px at 50% 50%, rgba(0,184,169,0.4) 0%, transparent 100%),
              radial-gradient(2px 2px at 15% 65%, rgba(255,255,255,0.2) 0%, transparent 100%),
              radial-gradient(2px 2px at 85% 15%, rgba(0,184,169,0.3) 0%, transparent 100%)
            `,
            animation: 'welcomeStarDrift 90s linear infinite reverse',
          }}
        />
      </div>

      {/* Concentric ring pulse animation */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="absolute w-[300px] h-[300px] rounded-full border border-[#00b8a9]/20"
          style={{ animation: 'welcomeRingPulse 4s cubic-bezier(0.4,0,0.6,1) infinite' }}
        />
        <div
          className="absolute w-[300px] h-[300px] rounded-full border border-[#00b8a9]/15"
          style={{ animation: 'welcomeRingPulse 4s cubic-bezier(0.4,0,0.6,1) infinite 1.3s' }}
        />
        <div
          className="absolute w-[300px] h-[300px] rounded-full border border-[#00b8a9]/10"
          style={{ animation: 'welcomeRingPulse 4s cubic-bezier(0.4,0,0.6,1) infinite 2.6s' }}
        />
      </div>

      {/* Central content */}
      <div className="relative z-10 text-center">
        {/* NORTH VALE title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-6xl md:text-7xl font-extralight text-white tracking-[0.3em] uppercase mb-4"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          Skewb
        </motion.h1>

        {/* LIVE */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-5xl md:text-6xl font-bold text-[#00b8a9] tracking-[0.15em] uppercase mb-8"
          style={{ textShadow: '0 0 40px rgba(0,184,169,0.5), 0 0 80px rgba(0,184,169,0.2)' }}
        >
          LIVE
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 1.0 }}
          className="text-slate-400 text-base md:text-lg tracking-wide max-w-lg mx-auto mb-16 font-light"
        >
          Interactive Digital Twin&ensp;&bull;&ensp;Skewb Product Portfolio
        </motion.p>

        {/* TAP TO EXPLORE */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1.0 }}
          className="inline-block"
        >
          <div
            className="text-sm uppercase tracking-[0.3em] text-[#00b8a9]/70 font-medium"
            style={{ animation: 'welcomeTapPulse 2.5s ease-in-out infinite' }}
          >
            Tap to Explore
          </div>
        </motion.div>
      </div>

      {/* Bottom attribution */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 text-center"
      >
        <p className="text-slate-600 text-xs uppercase tracking-[0.2em]">
          Powered by Skewb
        </p>
      </motion.div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes welcomeRingPulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }

        @keyframes welcomeStarDrift {
          0% {
            transform: translateY(0px) translateX(0px);
          }
          100% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes welcomeTapPulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </motion.div>
  );
}
